import { Alert, Empty, List, Typography } from 'antd'
import React from 'react'
import { Link } from 'react-router-dom'
import { createWorkOrder, workOrders } from 'src/services/workOrders'
import { store } from 'src/store/configureStore'
import t from '../../utils/translate'
import { displayNamesRequest } from 'src/store/actions/displayNames'
import {
  dashboardActivityCardTotalApi,
  dashboardProjectStateCountsApi,
  dashboardUsedIngredientsApi,
  dashboardWorkorderStateCountsApi
} from 'src/services/dashboard'
import { updateProfileApi } from 'src/services/login'
import { languages } from 'src/store/reducers/language'
import { updateNumericalCodeApi } from 'src/services/settings'
import { configsUpdate } from 'src/store/actions/configs'
import { updateProfileSuccess } from 'src/store/actions/login'
import { languageToggle } from 'src/store/actions/language'
import { celsureWoListApi, fetchCelsureProjectList, newCelsureProject, updateCelsureProject } from 'src/services/celsure'
import { fetchProjectList, newProject, updateProject } from 'src/services/projects'
import { fetchProjectListRequest } from 'src/store/actions/projects'
import { findProjectIdFromName, findUserIdFromUserName, findVersionFromModel, findWorkorderStage, isInternalEmail } from './convertHelper'
import WorkorderTable from './WorkordersTable'
import ModelsTable from './ModelsTable'
import PredictionsTable from './PredictionsTable'
import { getPredictionIdApi } from 'src/services/inverseModel'
import { fetchModelsConfigList, forwardPredListApi } from 'src/services/forwardModel'
import ProjectsTable from './ProjectsTable'
import { modelsConfigListRequest } from 'src/store/actions/formulate'
import { formulationFilterToolsImplementation, formulationFilterToolsSchema } from './tools/formulationFilter'
import { apiCallHelper, apiCallHelperOld } from './apiCallHelper'
import { inversePredictionToolsImplementation, inversePredictionToolsSchema } from './tools/inversePrediction'
import { jsonrepair } from 'jsonrepair'
import { StyledButton } from 'src/styled_components/StyledButton'
import { RedoOutlined } from '@ant-design/icons'
import { workOrdersRequest } from 'src/store/actions/workOrders'
import { AI_ASSISTANT_QUERIES_FEEDBACK_COLLECTION, firestoreDb } from 'src/utils/firebase'
import { collection, doc, setDoc } from 'firebase/firestore'
import { slackMessageTemplate } from './AiAssistantWrapper'
import { sendUserQuerySlackMessage } from 'src/services/aiAssist'
import jwtManager from 'src/utils/jwtManager'

const OPENAI_API_KEY = process.env.REACT_APP_OPENAI_API_KEY

const state = store.getState()
const isCelsure = state.login.loginResponse.project_type === 'celsure'


export type ToolsImplementationsType = {
  [key: string]: (
    args: Record<string, string>, 
  ) => Promise<{
    node: React.ReactNode
    content: string
  }>
}


const basicToolsImplementations: ToolsImplementationsType = {
  create_workorder: async (args: Record<string, string>) => {

    const { project_name } = args

    let project_id = findProjectIdFromName(project_name)

    if(!project_id){
      project_id = store.getState().projects.current
    }


    const otherArgs = {
      stages: [
        {
          name: 'Stage 1',
          description: '',
          data: [
            {
              type: 'formulation_stage',
              name: 'Formulation'
            },
            {
              type: 'processing_stage',
              name: 'Processing'
            },
            {
              type: 'characterization_stage',
              name: 'Characterization'
            },
            {
              type: 'properties_stage',
              name: 'Properties'
            }
          ]
        }
      ],
      work_order_seq: -1,
      work_order_dri: [],
      project_id: project_id
    }

    return await apiCallHelper({
      request: createWorkOrder,
      params: { ...otherArgs, ...args },
      additionalCall: () => {
        store.dispatch(displayNamesRequest({ backgroundFetch: true, inputs: ['material', 'application', 'grade', 'order_type'] }))
        store.dispatch(
          workOrdersRequest({
            project_ids: [project_id],
            is_table: true,
            page_num: 1,
            page_size: 20
          })
        )
      },
      nodeGenerator: (data: any, message: string) => (
        <Alert
          type="success"
          message={
            <Typography.Text>
              {message} {
                project_id === store.getState().projects.current 
                ? <Link to={`/work-orders/details/${data.work_order_id}`}>{t('common.view')}</Link> 
                : null
              }
            </Typography.Text>
          }
          banner
        />
      )
    })
  },

  get_used_ingredients: async (args: Record<string, string>) => {
    const { type } = args

    return await apiCallHelper({
      request: dashboardUsedIngredientsApi,
      params: {},
      nodeGenerator: (data: any, message: string) => {
        const ingredients = data[type]

        return (
          <List>
            {ingredients.map((ingredient: any, idx: number) => {
              const { name, count, project_count, workorder_count } = ingredient

              return (
                <List.Item key={idx}>
                  <Typography.Text>
                    {`${idx + 1}. ${name} - (${t("common.Total")} - ${count},  ${t('common.projects')} - ${project_count}, ${t('common.workOrders')} - ${workorder_count})`}
                  </Typography.Text>
                </List.Item>
              )
            })}
          </List>
        )
      }
    })
  },
  get_projects_count_by_status: async (args: Record<string, string>) => {
    const { status } = args

    return await apiCallHelper({
      request: dashboardProjectStateCountsApi,
      params: {},
      messageGenerator: (data: any, message: string) => {
        const count = data[status]
        return `${t('common.count')}: ${count}`
      }
    })
  },
  get_workorders_count_by_status: async (args: Record<string, string>) => {
    const { status } = args

    return await apiCallHelper({
      request: dashboardWorkorderStateCountsApi,
      params: {},
      messageGenerator: (data: any, message: string) => {
        const modifiedData = {
          total: data.total,
          ...data.stages
        }
        const count = modifiedData[status]
        return `${t('common.count')}: ${count}`
      }
    })
  },
  get_count_of_activities: async (args: Record<string, string>) => {
    const { type } = args

    return await apiCallHelper({
      request: dashboardActivityCardTotalApi,
      params: {},
      messageGenerator: (data: any, message: string) => {
        const count = data[type]
        return `${t('common.count')}: ${count}`
      },
      nodeGenerator: (data: any, message: string) => {
        return type === 'predictions_generated' ? (
          <Link to={`/ai-engine/history/properties_prediction`}>{t('dashboard.viewPredictionsHistory')}</Link>
        ) : type === 'total_trials' ? (
          <Link to={'/dashboard'}>{t('navBar.menuItem.dashboard')}</Link>
        ) : null
      }
    })
  },
  change_language: async (args: Record<string, string>) => {
    const { language } = args

    return await apiCallHelper({
      request: updateProfileApi,
      params: { lang: language },
      additionalCall: (data: any) => {
        store.dispatch(updateProfileSuccess({ ...data, lang: language }))
        store.dispatch(languageToggle(language))
      },
      messageGenerator: (data: any, message: string) => {
        return `${message}`
      }
    })
  },
  change_decimal_system: async (args: Record<string, string>) => {
    const { decimal_system } = args

    return await apiCallHelper({
      request: updateNumericalCodeApi,
      params: { code: decimal_system },
      additionalCall: (data: any) => {
        store.dispatch(configsUpdate(data))
      },
      messageGenerator: (data: any, message: string) => {
        return `${message}`
      }
    })
  },
  create_project: async (args: Record<string, string>) => {
    const { name, category } = args

    return await apiCallHelper({
      request: isCelsure ? newCelsureProject : newProject,
      params: { name, category },
      additionalCall: () => {
        store.dispatch(fetchProjectListRequest({ set: true }))
      },
      nodeGenerator: (data: any, message: string) => (
        <Alert
          type="success"
          message={
            <Typography.Text>
              {message} <Link to={`/projects/details/${data.project_id}`}>{t('common.view')}</Link>
            </Typography.Text>
          }
          banner
        />
      )
    })
  },
  change_project_status: async (args: Record<string, string>) => {
    const { project_name, status, reason } = args

    const project_id = findProjectIdFromName(project_name)

    if (!project_id) {
      return {
        node: <Alert type="error" message={t('common.error')} banner />,
        content: t('common.error')
      }
    }

    return await apiCallHelper({
      request: isCelsure ? updateCelsureProject : updateProject,
      params: { project_id, status, reason },
      additionalCall: () => {
        store.dispatch(fetchProjectListRequest({ set: true }))
      },
      messageGenerator: (data: any, message: string) => {
        return `${message}`
      }
    })
  },
  get_workorders: async (args: Record<string, any>) => {
    const projects = store.getState().projects.projectList
    const { project_name, work_order_name, work_order_stage, created_by } = args

    const project_id = project_name?.map((name: string) => findProjectIdFromName(name))?.filter((id: any) => id !== null && id !== undefined)
    const stage = work_order_stage?.map((stage: string) => findWorkorderStage(stage))?.filter((id: any) => id !== null && id !== undefined)
    const created_by_id = created_by?.map((author: string) => findUserIdFromUserName(author))?.filter((id: any) => id !== null && id !== undefined)

    return await apiCallHelper({
      request: isCelsure ? celsureWoListApi : workOrders,
      params: {
        is_table: true,
        project_ids: projects.map((project: any) => project.project_id)
      },
      nodeGenerator: (data: any, message: string) => {
        const tableDataSource = data.map((item: any) => {
          return {
            work_order_id: item.work_order_id,
            work_order_name: item.work_order_name,
            work_order_stage: item.work_order_stage,
            created_by: item.created_by,
            project_id: item.project_id
          }
        })

        return data.length > 0 ? (
          <WorkorderTable
            tableDataSource={tableDataSource}
            params={{
              work_order_name,
              work_order_stage: stage,
              created_by: created_by_id,
              project_id
            }}
          />
        ) : (
          <Empty description={t('common.noWorkOrdersFoundInThisProject')} />
        )
      }
    })
  },
  get_ai_ml_models: async (args: Record<string, any>) => {
    const { objective, project_name, created_by } = args

    const project_id = project_name?.map((name: string) => findProjectIdFromName(name))?.filter((id: any) => id !== null && id !== undefined)
    const created_by_id = created_by?.map((author: string) => findUserIdFromUserName(author))?.filter((id: any) => id !== null && id !== undefined)

    return await apiCallHelperOld({
      request: fetchModelsConfigList,
      params: null,
      nodeGenerator: (data: any) => {
        const tableDataSource = data.map((item: any) => {
          return {
            objective: item.objective,
            created_by: item.created_by,
            is_inverse: item.is_inverse,
            project_id: item.project_id,
            version: item.version
          }
        })

        return (
          <ModelsTable
            tableDataSource={tableDataSource}
            params={{
              objective,
              created_by: created_by_id,
              project_id: project_id
            }}
          />
        )
      }
    })
  },
  get_projects: async (args: Record<string, any>) => {
    const { name, status, category, created_by } = args

    const project_id = args?.project_name?.map((name: string) => findProjectIdFromName(name))?.filter((id: any) => id !== null && id !== undefined)
    const created_by_id = created_by?.map((author: string) => findUserIdFromUserName(author))?.filter((id: any) => id !== null && id !== undefined)

    return await apiCallHelper({
      request: isCelsure ? fetchCelsureProjectList : fetchProjectList,
      params: {},
      nodeGenerator: (data: any, message: string) => {
        const tableDataSource = data.map((item: any) => {
          return {
            project_id: item.project_id,
            name: item.name,
            category: item.category,
            created_by: item.created_by,
            status: item.status
          }
        })
        return (
          <ProjectsTable
            tableDataSource={tableDataSource}
            params={{
              name,
              status,
              category,
              created_by: created_by_id,
              project_id
            }}
          />
        )
      }
    })
  },
  get_forward_inverse_predictions: async (args: Record<string, any>) => {
    const { type, objective, is_multistage, project_name, created_by, model_name } = args
    const project_id = project_name?.map((name: string) => findProjectIdFromName(name))?.filter((id: any) => id !== null && id !== undefined)
    const created_by_id = created_by?.map((author: string) => findUserIdFromUserName(author))?.filter((id: any) => id !== null && id !== undefined)
    const version = model_name?.map((model: string) => findVersionFromModel(model))?.filter((id: any) => id !== null && id !== undefined)

    return await apiCallHelper({
      request: type === 'inverse' ? getPredictionIdApi : forwardPredListApi,
      params: {},
      additionalCall: () => {
        store.dispatch(modelsConfigListRequest())
      },
      nodeGenerator: (data: any, message: string) => {
        const tableDataSource = (type === 'inverse' ? data : data?.predictions_list || [])?.map((item: any) => {
          return {
            objective: type === 'inverse' ? item?.title || '' : item?.objective || '',
            created_by: item.user_id,
            is_multistage: item.is_multistage,
            project_id: item.project_id,
            version: item.version,
            prediction_id: item.prediction_id
          }
        })

        return (
          <PredictionsTable
            tableDataSource={tableDataSource}
            params={{
              type,
              objective,
              is_multistage,
              created_by: created_by_id,
              project_id,
              version
            }}
            type={type === 'forward' ? 'properties_prediction' : type === 'inverse' ? 'formulation_prediction' : 'properties_prediction'}
          />
        )
      }
    })
  },
  ...formulationFilterToolsImplementation(),
  ...inversePredictionToolsImplementation(),
  handle_unknown_request: async (args: Record<string, any>) => {
    const { query } = args

    const state = store.getState()

    const { user_id, user_name, company, company_id, user_email } = state.login.loginResponse

    const newCityRef = doc(collection(firestoreDb, AI_ASSISTANT_QUERIES_FEEDBACK_COLLECTION))
    await setDoc(newCityRef, {
      user_id,
      user_name,
      company,
      company_id,
      query,
      created_at: new Date().toString(),
      environment: process.env.REACT_APP_ENV || 'dev'
    })

    const defaultHeaders = store.getState().defaultHeaders || {}
    const headers = { ...defaultHeaders, token: jwtManager.getToken() }

    if(!isInternalEmail(user_email) && process.env.REACT_APP_ENV === 'production'){
      await sendUserQuerySlackMessage(slackMessageTemplate(user_name, query, company, new Date().toLocaleString(), process.env.REACT_APP_ENV || 'dev', true), headers)
    }
    return {
      node: <Alert type="error" message={query} banner />,
      content: `${'query'}: ${query}`
    }
  }
}

const basicTools = () => {
  const projectNames = store.getState().projects.projectList.map((project: any) => project.name)

  const stageMap = store.getState().displayNames.data?.stages

  return [
    {
      type: 'function',
      function: {
        name: 'create_workorder',
        description: 'Create a new workorder',
        parameters: {
          type: 'object',
          properties: {
            project_name: {
              type: 'string',
              enum: projectNames,
              description: 'If value is not from enum provided ask them select the project which is already created or to create that project first or are they okay with selecting existing project?'
            },
            work_order_name: { type: 'string' },
            application: { type: 'string' },
            material: { type: 'string' },
            work_order_desc: { type: 'string' },
            grade: { type: 'string' }
          },
          required: ['work_order_name', 'material']
        }
      }
    },
    {
      type: 'function',
      function: {
        name: 'get_used_ingredients',
        description: 'Get 5 highly or least used ingredients',
        parameters: {
          type: 'object',
          properties: {
            type: {
              type: 'string',
              enum: ['least_used_ingredients', 'most_used_ingredients']
            }
          },
          required: ['type']
        }
      }
    },
    {
      type: 'function',
      function: {
        name: 'get_projects_count_by_status',
        description: 'Get the number of projects by status',
        parameters: {
          type: 'object',
          properties: {
            status: {
              type: 'string',
              enum: ['total', 'ongoing', 'completed', 'archived']
            }
          },
          required: ['status']
        }
      }
    },
    {
      type: 'function',
      function: {
        name: 'get_workorders_count_by_status',
        description: 'Get the number of workorders by status',
        parameters: {
          type: 'object',
          properties: {
            status: {
              type: 'string',
              enum: [
                'total',
                'work_order_initiated',
                'formulation_stage',
                'processing_stage',
                'characterization_stage',
                'properties_stage',
                'report_preview',
                'closed',
                'work_order_status'
              ]
            }
          },
          required: ['status']
        }
      }
    },
    {
      type: 'function',
      function: {
        name: 'get_count_of_activities',
        description: 'Get the number of my activities by type',
        parameters: {
          type: 'object',
          properties: {
            type: {
              type: 'string',
              enum: ['projects_created', 'work_orders_created', 'datasets_uploaded', 'predictions_generated', 'total_trials']
            }
          },
          required: ['type']
        }
      }
    },
    {
      type: 'function',
      function: {
        name: 'change_language',
        description: 'Change the language of the application, always perform this task without fail I see response that language is already in "English" which is not, take care of that',
        parameters: {
          type: 'object',
          properties: {
            language: {
              type: 'string',
              enum: languages
            }
          },
          required: ['language']
        }
      }
    },
    {
      type: 'function',
      function: {
        name: 'change_decimal_system',
        description: 'Change the decimal/numerical system of the application default to en_US',
        parameters: {
          type: 'object',
          properties: {
            decimal_system: {
              type: 'string',
              enum: ['en_US', 'de_DE']
            }
          }
        }
      }
    },
    {
      type: 'function',
      function: {
        name: 'create_project',
        description: 'Create a new project',
        parameters: {
          type: 'object',
          properties: {
            name: { type: 'string' },
            category: { type: 'string' }
          },
          required: ['name', 'category']
        }
      }
    },
    {
      type: 'function',
      function: {
        name: 'change_project_status',
        description: 'Change the status of a project',
        parameters: {
          type: 'object',
          properties: {
            project_name: {
              type: 'string',

              enum: projectNames
            },
            reason: { type: 'string' },
            status: {
              type: 'string',
              enum: ['in_progress', 'completed', 'archived'],
              description: 'While responding to the user use refined readable version of enum values (not _s)'
            }
          },
          required: ['project_name', 'status', 'reason']
        }
      }
    },
    {
      type: 'function',
      function: {
        name: 'get_workorders',
        description: 'Get all workorders',
        parameters: {
          type: 'object',
          properties: {
            project_name: {
              type: 'array',
              items: {
                type: 'string',
                enum: projectNames
              }
            },
            work_order_name: {
              type: 'string'
            },
            work_order_stage: {
              type: 'array',
              items: {
                type: 'string',
                enum: Object.keys(stageMap || {})
              }
            },
            created_by: {
              type: 'array',
              items: {
                type: 'string'
              }
            }
          },
          required: []
        }
      }
    },
    {
      type: 'function',
      function: {
        name: 'get_ai_ml_models',
        description: 'Get all AI/ML models',
        parameters: {
          type: 'object',
          properties: {
            objective: { type: 'string', description: 'This is objective of model, it is also called model name, keep that in mind.' },
            project_name: {
              type: 'array',
              items: {
                type: 'string',
                enum: projectNames
              }
            },
            created_by: {
              type: 'array',
              items: {
                type: 'string'
              }
            }
          },
          required: []
        }
      }
    },
    {
      type: 'function',
      function: {
        name: 'get_projects',
        description: 'Get all projects',
        parameters: {
          type: 'object',
          properties: {
            name: {
              type: 'string'
            },
            status: {
              type: 'array',
              items: {
                type: 'string',
                enum: ['in_progress', 'completed', 'archived'],
                description: 'While responding to the user use refined readable version of enum values (not _s)'
              }
            },
            category: { type: 'array', items: { type: 'string' } },
            created_by: {
              type: 'array',
              items: {
                type: 'string'
              }
            }
          },
          required: []
        }
      }
    },
    {
      type: 'function',
      function: {
        name: 'get_forward_inverse_predictions',
        description: 'Unrelated with prepare_inverse_prediction_data, Get/find/list forward/inverse predictions this is different from performing inverse which is done by prepare_inverse_prediction_data',
        parameters: {
          type: 'object',
          properties: {
            type: { type: 'string', enum: ['forward', 'inverse'], description: 'Try to extract type from query itself always if not clear then only ask user what type it is' },
            objective: { type: 'string' },
            is_multistage: { type: 'boolean' },
            project_name: {
              type: 'array',
              items: {
                type: 'string',
                enum: projectNames
              }
            },
            created_by: {
              type: 'array',
              items: {
                type: 'string'
              }
            },
            model_name: {
              type: 'array',
              items: {
                type: 'string'
              }
            }
          },
          required: []
        }
      }
    },
    ...formulationFilterToolsSchema(),
    ...inversePredictionToolsSchema(),
    {
      type: 'function',
      function: {
        name: 'handle_unknown_request',
        description:
          'Handles any user request that does not match the other functions, anything not in the list should be passed here and mention that it is not implemented but feedback has been sent to the team',
        parameters: {
          type: 'object',
          properties: {
            query: {
              type: 'string'
            }
          },
          required: ['query']
        }
      }
    }
  ]
}

const toolsImplementations: ToolsImplementationsType = {
  ...basicToolsImplementations
}

export async function callOpenAI(messages: any[], onToken: (token: string, node?: React.ReactNode) => void) {
  const res = await fetch('https://api.openai.com/v1/chat/completions', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${OPENAI_API_KEY}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      model: 'gpt-4o',
      messages: [
        {
          role: 'system',
          content:
            "You are a helpful assistant. Please respond naturally to the user before calling any functions. If a function needs to be called, first say what you're doing in a conversational way. if no functions available to call report it accordingly."
        },
        ...messages
      ],
      tools: [...basicTools()],
      tool_choice: 'auto',
      stream: true
    })
  })

  if (!res.ok || !res.body) {
    throw new Error('OpenAI stream request failed')
  }

  const reader = res.body.getReader()
  const decoder = new TextDecoder('utf-8')
  let functionCall: any[] = []

  while (true) {
    const { done, value } = await reader.read()
    if (done) break

    const chunk = decoder.decode(value, { stream: true })

    const lines = chunk
      .split('\n')
      .filter((line) => line.trim().startsWith('data:'))
      .map((line) => line.replace('data: ', '').trim())

    for (const line of lines) {
      if (line === '[DONE]') {
        if (functionCall) {
          functionCall.forEach(async (call) => {
            try{
              const { name, arguments: argStr, toolCall } = call
            const args = JSON.parse(jsonrepair(argStr || '{}'))

            if (toolsImplementations[name]) {
              const { node, content } = await toolsImplementations[name](args)
              let output = ''
              content && await callOpenAIAgain(
                [
                  ...messages,
                  {
                    role: 'assistant',
                    content: null,
                    tool_calls: [toolCall],
                    refusal: null,
                    annotations: []
                  },
                  {
                    role: 'tool',
                    tool_call_id: toolCall.id,
                    content: argStr + content
                  }
                ],
                (token) => {
                  output += token
                }
              )
              onToken('\n' + output, node) // append result of function call
              return
            } else {
              onToken(`\nFunction '${name}' is not implemented.`)
              return
            }
            }catch(e){
              onToken('\n', 
              <Alert type="error" message={`${t('error.operationalProblems')}`} banner >
                <StyledButton
                icon={<RedoOutlined/>}
                onClick={async ()=>{
                  await callOpenAI(
                    messages,
                    onToken
                  )
                }}
                >
                  {t('common.retry')}
                </StyledButton>
              </Alert>
              )
            }
          })
        }
        return
      }

      try {
        const parsed = JSON.parse(line)
        const delta = parsed.choices[0].delta

        // Handle streamed tool call (function call in new format)
        if (delta?.tool_calls) {
          const toolCall = delta.tool_calls[0] // assuming there's only one tool call per message
          const { name, arguments: argStr } = toolCall.function

          if (!functionCall) functionCall = []

          if (name) {
            const newFuncationCall = {
              name,
              arguments: '',
              toolCall
            }
            functionCall.push(newFuncationCall)
          }

          if (argStr) {
            const idx = functionCall.length - 1
            functionCall[idx].arguments += argStr
          }

          // If you need to handle function execution here, you can process it accordingly
        }

        // Handle streamed content
        if (delta?.content) {
          onToken(delta.content)
        }
      } catch (e) {
        console.error('Error parsing stream chunk', e)
      }
    }
  }
}

export async function callOpenAIAgain(messages: any[], onToken: (token: string) => void) {
  const res = await fetch('https://api.openai.com/v1/chat/completions', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${OPENAI_API_KEY}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      model: 'gpt-4o',
      messages: [
        ...messages
      ],
      tools: [...basicTools()],
      tool_choice: 'none',
      stream: true
    })
  })

  if (!res.ok || !res.body) {
    throw new Error('OpenAI stream request failed')
  }

  const reader = res.body.getReader()
  const decoder = new TextDecoder('utf-8')

  while (true) {
    const { done, value } = await reader.read()
    if (done) break

    const chunk = decoder.decode(value, { stream: true })

    const lines = chunk
      .split('\n')
      .filter((line) => line.trim().startsWith('data:'))
      .map((line) => line.replace('data: ', '').trim())

    for (const line of lines) {
      if (line === '[DONE]') {
        return
      }



      try {
        const parsed = JSON.parse(line)
        const delta = parsed.choices[0].delta

        // Handle streamed content
        if (delta?.content) {
          onToken(delta.content)
        }
      } catch (e) {
        console.error('Error parsing stream chunk', e)
      }
    }
  }
}
